<script lang="ts">
  interface LeaderboardEntry {
    name: string;
    score: number;
    wins: number;
    timePlayed: number;
    rank: number;
  }
  
  let { currentPlayer = 'Guest', currentScore = 0 } = $props();
  
  // Simulated leaderboard data (in production, this would come from a backend)
  const leaderboardData: LeaderboardEntry[] = [
    { name: 'GreenChamp99', score: 15420, wins: 87, timePlayed: 320, rank: 1 },
    { name: 'EcoWarrior', score: 12850, wins: 72, timePlayed: 280, rank: 2 },
    { name: 'ChessMaster', score: 11200, wins: 65, timePlayed: 250, rank: 3 },
    { name: 'LudoKing', score: 9800, wins: 58, timePlayed: 220, rank: 4 },
    { name: 'BlackJackPro', score: 8500, wins: 51, timePlayed: 190, rank: 5 },
    { name: 'GreenGamer', score: 7200, wins: 45, timePlayed: 160, rank: 6 },
    { name: 'EcoHero', score: 6100, wins: 38, timePlayed: 140, rank: 7 },
    { name: 'ClimateChamp', score: 5400, wins: 32, timePlayed: 120, rank: 8 },
    { name: 'WorldCupFan', score: 4800, wins: 28, timePlayed: 100, rank: 9 },
    { name: 'GrnHausFan', score: 4200, wins: 24, timePlayed: 85, rank: 10 },
  ];
  
  // Add current player if they have a score
  $effect(() => {
    if (currentScore > 0 && !leaderboardData.some(p => p.name === currentPlayer)) {
      // In production, this would submit to backend
      console.log('New score submitted:', { name: currentPlayer, score: currentScore });
    }
  });
</script>

<section class="leaderboard-section">
  <h2>🏆 Real-Time Leaderboard</h2>
  <p class="subtitle">Compete globally! Climb the ranks with consecutive wins and time played.</p>
  
  <div class="leaderboard-container">
    <table class="leaderboard-table">
      <thead>
        <tr>
          <th>Rank</th>
          <th>Player</th>
          <th>Score</th>
          <th>Wins</th>
          <th>Time Played</th>
        </tr>
      </thead>
      <tbody>
        {#each leaderboardData as player}
          <tr class="leaderboard-row {player.name === currentPlayer ? 'current-player' : ''}">
            <td class="rank-cell">
              {#if player.rank === 1}
                🥇
              {:else if player.rank === 2}
                🥈
              {:else if player.rank === 3}
                🥉
              {:else}
                #{player.rank}
              {/if}
            </td>
            <td class="name-cell">{player.name}</td>
            <td class="score-cell">{player.score.toLocaleString()}</td>
            <td class="wins-cell">{player.wins}</td>
            <td class="time-cell">{player.timePlayed}m</td>
          </tr>
        {/each}
      </tbody>
    </table>
    
    {#if currentScore > 0}
      <div class="your-score">
        <p>Your Score: <strong>{currentScore.toLocaleString()}</strong></p>
        <p>Keep playing to climb the leaderboard! 🌿</p>
      </div>
    {/if}
  </div>
  
  <div class="leaderboard-info">
    <div class="info-card">
      <span class="info-icon">🎯</span>
      <h4>How to Rank Up</h4>
      <p>Win consecutive games and spend more time playing to earn points!</p>
    </div>
    <div class="info-card">
      <span class="info-icon">♻️</span>
      <h4>Green Bonus</h4>
      <p>Eco-friendly gameplay earns bonus points for climate awareness!</p>
    </div>
    <div class="info-card">
      <span class="info-icon">🏆</span>
      <h4>World Cup 2026</h4>
      <p>Special rankings during World Cup season with exclusive rewards!</p>
    </div>
  </div>
</section>

<style>
  .leaderboard-section {
    background: linear-gradient(135deg, #ffffff 0%, #f0fff4 100%);
    border-radius: 30px;
    padding: 50px 30px;
    box-shadow: 0 15px 50px rgba(46, 204, 113, 0.15);
  }
  
  .leaderboard-section h2 {
    text-align: center;
    font-size: 2.5rem;
    color: #2d5016;
    margin-bottom: 15px;
  }
  
  .subtitle {
    text-align: center;
    color: #5a7a5a;
    margin-bottom: 40px;
    font-size: 1.1rem;
  }
  
  .leaderboard-container {
    overflow-x: auto;
  }
  
  .leaderboard-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 5px 20px rgba(46, 204, 113, 0.1);
  }
  
  .leaderboard-table thead {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
  }
  
  .leaderboard-table th {
    padding: 18px 15px;
    text-align: left;
    font-weight: 700;
    font-size: 1rem;
  }
  
  .leaderboard-table td {
    padding: 15px;
    border-bottom: 1px solid #e8f5e9;
    color: #2d5016;
  }
  
  .leaderboard-row:hover {
    background: #f0fff4;
  }
  
  .leaderboard-row.current-player {
    background: linear-gradient(90deg, rgba(46, 204, 113, 0.2), rgba(39, 174, 96, 0.1));
    font-weight: 700;
  }
  
  .rank-cell {
    font-size: 1.3rem;
    text-align: center;
  }
  
  .name-cell {
    font-weight: 600;
  }
  
  .score-cell {
    font-weight: 700;
    color: #2ecc71;
  }
  
  .wins-cell {
    text-align: center;
  }
  
  .time-cell {
    text-align: right;
  }
  
  .your-score {
    margin-top: 25px;
    padding: 20px;
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    border-radius: 15px;
    text-align: center;
  }
  
  .your-score strong {
    font-size: 1.5rem;
  }
  
  .leaderboard-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 25px;
    margin-top: 40px;
  }
  
  .info-card {
    background: white;
    padding: 25px;
    border-radius: 15px;
    text-align: center;
    border: 2px solid #e8f5e9;
    transition: all 0.3s ease;
  }
  
  .info-card:hover {
    transform: translateY(-5px);
    border-color: #2ecc71;
    box-shadow: 0 10px 30px rgba(46, 204, 113, 0.2);
  }
  
  .info-icon {
    font-size: 2.5rem;
    display: block;
    margin-bottom: 15px;
  }
  
  .info-card h4 {
    color: #2d5016;
    margin-bottom: 10px;
    font-size: 1.2rem;
  }
  
  .info-card p {
    color: #5a7a5a;
    line-height: 1.6;
    font-size: 0.95rem;
  }
  
  @media (max-width: 768px) {
    .leaderboard-section {
      padding: 30px 15px;
    }
    
    .leaderboard-table th,
    .leaderboard-table td {
      padding: 12px 8px;
      font-size: 0.9rem;
    }
  }
</style>
